/**
 * Creates the Likert Scale field. Iterates the grade N times
 *
 * @prop {bool} editing - Indicated whether the user is in editing mode
 * @prop {object} input - Contains the current value for the field and onChange method for changing the value
 * @prop {function} localeMessage - Changes message id to corresponding locale string
 * @prop {int} max - Maximum amount of grades in the field. Iterates over this
 * @prop {string} minLabel - Label id to be displayed under minimum number
 * @prop {string} maxLabel - Label id to be displayed under maximum number
 * @prop {bool} startFromZero - if true ==> grades start from 0
 * @prop {bool} invertedScale - if true ==> grades decend from max to 1 (max-1 to 0 if startFromZero)
 * @prop {string array 1..max} allLabels - Labels for each option - optional prop, if not provided only minLabel and maxLabel are printed
 *
 * @returns Likert scale with N number of grades
 */

import React from 'react';
import PropTypes from 'prop-types';
import { times } from 'ramda';
import { Row, Col } from 'react-flexbox-grid';

import LikertGrade from 'components/LikertGrade';
import classes from './Field.less';

const calculateGradeValue = (grade, startFromZero, invertedScale, max) => {
  let value = invertedScale ? max - grade : grade + 1;
  value = startFromZero ? value - 1 : value;
  return value;
};

const LikertField = ({
  editing,
  input,
  localeMessage,
  max,
  minLabel,
  maxLabel,
  startFromZero,
  invertedScale,
  allLabels,
}) => (
  <Row start="xs" between="sm" className={classes.fieldWrapper}>
    {times(
      grade => (
        <Col xs={12} sm key={grade}>
          <LikertGrade
            gradeNumber={calculateGradeValue(grade, startFromZero, invertedScale, max)}
            descText={
              allLabels
                ? localeMessage(allLabels[grade])
                : grade === 0
                  ? localeMessage(minLabel)
                  : grade === max - 1
                    ? localeMessage(maxLabel)
                    : null
            }
            selected={calculateGradeValue(grade, startFromZero, invertedScale, max) === input.value}
            onClick={editing ? input.onChange : null}
          />
        </Col>
      ),
      max,
    )}
  </Row>
);

LikertField.propTypes = {
  editing: PropTypes.bool,
  input: PropTypes.object,
  localeMessage: PropTypes.func,
  max: PropTypes.number,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  startFromZero: PropTypes.bool,
  invertedScale: PropTypes.bool,
  allLabels: PropTypes.arrayOf(PropTypes.string),
};

export default LikertField;
